import React from 'react';
import moment from 'moment';
import { startCase, isEqual, map, capitalize } from 'lodash';
import DayPicker from 'react-day-picker';
import { formatDate } from 'react-day-picker/moment';
import { withTranslation } from 'react-i18next';
import { handleMomenti18n, checkIfIsMobile } from '@helpers/helpers';
import { noteModalOpening, playExit } from '@helpers/sheetMotion';
import classNames from 'classnames';
// import 'moment/locale/pt'; // Include the locale utils designed for moment
import { Icon, Button } from '../../elements';
import SheetGrabber from '../SheetGrabber/SheetGrabber';
import { ErrorBoundaryView } from '@blocks';
import './CustomDayPicker.scss';

const WEEKDAYS_SHORT_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAYS_SHORT_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Days of the selected date's ISO week (Mon → Sun) to highlight in week mode.
// Hidden weekend days stay unhighlighted so the bar matches the days the week
// calendar actually renders.
export const getWeekHighlightDays = (selectedDate: moment.Moment, disableSaturday: ?boolean, disableSunday: ?boolean): Array<Date> => {
  const weekStart = moment(selectedDate).isoWeekday(1);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = weekStart.clone().add(i, 'days');
    const hidden = (day.isoWeekday() === 6 && disableSaturday) || (day.isoWeekday() === 7 && disableSunday);
    if (!hidden) days.push(day.toDate());
  }
  return days;
};

type Props = {
  showButtons?: boolean,
  withOverlay?: boolean,
  advancedMode?: boolean,
  isOpen?: boolean,
  showToday?: boolean,
  selectedDate: moment.Moment,
  onSelectDay: ?Function,
  // Fired when the displayed month changes: prev/next arrows and month picks.
  // Some consumers (Agenda filters) commit this straight to app state, so it
  // must never carry provisional drill-down steps — those use the props below.
  onMonthChange: ?Function,
  // Fired only when a month is deliberately picked in the month picker.
  onSelectMonth?: Function,
  // Fired when a year is picked in the year drill-down. Provisional: the
  // month picker opens next, so this is not a completed month change.
  onSelectYear?: Function,
  onCancel?: Function,
  onAccept?: Function,
  acceptOnSelect?: boolean,
  i18n: Object,
  disableSaturday?: boolean,
  disableSunday?: boolean,
  // Highlight the whole ISO week of selectedDate instead of the single day
  // (used by the agenda sidebar when the calendar is in week view).
  highlightWeek?: boolean,
};

// The day number sits in its own circle so the table cell can stay wide
const renderDay = (day: Date) => <span className="dayNumber">{day.getDate()}</span>;


type State = {
  yearMode: boolean,
  monthMode: boolean,
  yearStart: number,
  yearEnd: number,
  montSelectYear: number,
  selectedYear: number,
};

class CustomDayPicker extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const propsDate = moment(props.selectedDate);
    const selectedYear = propsDate.isValid() ? propsDate.year() : moment().year();
    this.state = {
      yearMode: false,
      monthMode: false,
      yearStart: selectedYear - 6,
      yearEnd: selectedYear + 6,
      montSelectYear: selectedYear,
      selectedYear,
    };
  }

  // Recenter the year/month pickers when the selected date moves to another
  // year. `selectedYear` mirrors the last seen prop year so this only fires
  // on real year transitions, like the componentWillReceiveProps it replaced.
  static getDerivedStateFromProps(props: Props, state: State) {
    const nDate = moment(props.selectedDate);
    if (nDate.isValid() && nDate.year() !== state.selectedYear) {
      return {
        selectedYear: nDate.year(),
        yearStart: nDate.year() - 6,
        montSelectYear: nDate.year(),
        yearEnd: nDate.year() + 6,
      };
    }
    return null;
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  // Closing on the phone: the sheet slides down instead of vanishing (a copy plays it; helpers/sheetMotion)
  // Opening: a sheet it replaces in the same update (the record's Opções list) doesn't slide out under it
  getSnapshotBeforeUpdate(prevProps: Props) {
    if (prevProps.isOpen && !this.props.isOpen) playExit(this.overlay);
    if (!prevProps.isOpen && this.props.isOpen && this.props.withOverlay) noteModalOpening();
    return null;
  }

  // An overlay picker stays mounted while closed: it always reopens on the day grid,
  // even if it was closed from the month or year grid
  componentDidUpdate(prevProps: Props) {
    if (this.props.isOpen && !prevProps.isOpen && (this.state.monthMode || this.state.yearMode)) {
      this.setCalendarMode();
    }
  }

  componentWillUnmount() {
    if (this.props.isOpen) playExit(this.overlay);
  }

  renderMonthPicker = () => {
    const activeMonth = moment(this.props.selectedDate).month();
    const months = this.props.i18n.language === 'es' ?
      ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] :
      ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];


    return (
      <div className="ymPicker" >
        <div className="ymToolbar">
          <button type="button" className="navButton" onClick={this.onPaginateMonthYear(false)}><Icon iconName="arrow-left" size={18} /></button>
          <div className="ymLabel clickable" onClick={this.handleCaptionClick('year')}>{this.state.montSelectYear}</div>
          <button type="button" className="navButton" onClick={this.onPaginateMonthYear(true)}><Icon iconName="arrow-right" size={18} /></button>
        </div>
        <div className="ymList">
          {map(months, (month: string | null, index: string) => (
            <div key={`month-${index}`} className={`ymItem ${activeMonth === index ? 'active' : ''}`} onClick={this.onSetMonth(index, this.state.montSelectYear)}>{month}</div>
          ))}
        </div>
      </div>
    );
  };

  renderYearPicker = () => {
    const { yearStart, yearEnd } = this.state;

    const years = [];

    for (let y = yearStart; y < yearEnd; y++) {
      years.push(<div key={`year-${y}`} className={`ymItem ${this.state.montSelectYear === y ? 'active' : ''}`} onClick={this.onSetYear(y)}>{y}</div>);
    }

    return (
      <div className="ymPicker" >
        <div className="ymToolbar">
          <button type="button" className="navButton" onClick={this.onPaginateYear(false)}><Icon iconName="arrow-left" size={18} /></button>
          <div className="ymLabel">{yearStart} - {yearEnd - 1}</div>
          <button type="button" className="navButton" onClick={this.onPaginateYear(true)}><Icon iconName="arrow-right" size={18} /></button>
        </div>
        <div className="ymList">{years}</div>
      </div>
    );
  };

  render() {
    handleMomenti18n(this.props.i18n, moment);

    const {
      showButtons,
      withOverlay,
      isOpen,
      acceptOnSelect,
      disableSaturday,
      disableSunday,
      showToday,
      advancedMode,
      highlightWeek,
    } = this.props;

    const disabledDaysOfWeek = [];
    if (disableSunday) disabledDaysOfWeek.push(0);
    if (disableSaturday) disabledDaysOfWeek.push(6);

    const {
      monthMode, yearMode,
    } = this.state;


    // The arrows are 40px buttons: easy to hit with a thumb
    const customNavbar = (data) => (
      <div className="customNavElement">
        <button type="button" className="navButton prevButton" onClick={this.onChangeMonth.bind(this, data, 'prev')}>
          <Icon className="icon" iconName="arrow-left" size={18} />
        </button>
        <button type="button" className="navButton nextButton" onClick={this.onChangeMonth.bind(this, data, 'next')}>
          <Icon className="icon" iconName="arrow-right" size={18} />
        </button>
      </div>
    );

    // "Outubro 2026"; with the drill-down, a chevron says the caption opens the month and year pickers
    const customCaption = (data) => {
      const month = capitalize(moment(data.date).format('MMMM'));
      return (
        <div className="customCaptionElement">
          <span className={classNames('captionLabel', { clickable: advancedMode })} onClick={this.handleCaptionClick('month')}>
            {month}&nbsp;{`${startCase(formatDate(data.date, 'YYYY', 'pt'))}`}
            {advancedMode && <Icon iconName="arrow-down" className="icon captionChevron" size={14} />}
          </span>
        </div>
      );
    };

    let selectedDate = moment(this.props.selectedDate);
    selectedDate = selectedDate.isValid() ? selectedDate : moment();

    // calculate selected date
    const currentYear = selectedDate.year();
    const currentMonth = selectedDate.month();

    const modifiers = {
      highlighted: this.props.highlightWeek
        ? getWeekHighlightDays(selectedDate, this.props.disableSaturday, this.props.disableSunday)
        : selectedDate.toDate(), // selected date
    };

    // The phone (Cordova under 1020px): the overlay picker is a bottom sheet, which can be pulled down to close,
    // like Cancelar. Read from the window only when open, so the closed pickers (one per timeline record) don't
    // each subscribe to the store
    const isSheet = Boolean(withOverlay && isOpen && checkIfIsMobile({ window: { width: window.innerWidth } }));

    const dayPicker = (
      <div className="dayPickerContainer">
        <div className={classNames('customDayPickerWrapper', { highlightWeek })}>
          {isSheet && <SheetGrabber onClose={this.onCancel} />}
          <ErrorBoundaryView>
            {!monthMode && !yearMode && (
              <DayPicker
                showOutsideDays
                modifiers={modifiers}
                className="customDayPicker"
                renderDay={renderDay}
                captionElement={customCaption}
                disabledDays={disabledDaysOfWeek.length ? { daysOfWeek: disabledDaysOfWeek } : undefined}
                navbarElement={customNavbar}
                firstDayOfWeek={1}
                month={new Date(currentYear, currentMonth)} // month that is currentlly shown
                onDayClick={this.handleSelectDay.bind(this)}
                weekdaysShort={this.props.i18n.language === 'es' ? WEEKDAYS_SHORT_ES : WEEKDAYS_SHORT_PT}
                locale={this.props.i18n.language === 'es' ? 'es' : 'pt'}
              />
            )}
          </ErrorBoundaryView>
          {!monthMode && yearMode && this.renderYearPicker()}
          {monthMode && !yearMode && this.renderMonthPicker()}
          {showButtons &&
          <div className="actionButtonsWrapper">
            {showToday && (
              <Button
                onClick={this.onTodayClick}
                className="todayButton"
                text="Hoje"
              />
            )}
            <div className="actionButtonsEnd">
              <Button
                onClick={this.onCancel.bind(this)}
                className="cancelButton"
                text="Cancelar"
              />
              {!acceptOnSelect &&
                <Button
                  onClick={this.onAccept.bind(this)}
                  className="acceptButton primary"
                  text="OK"
                />
              }
            </div>
          </div>
        }
        </div>
      </div>
    );

    let element;
    if (withOverlay) {
      element = isOpen ? (
        <div className={classNames('dayPickerOverlay', { isMobile: isSheet })} ref={this.setOverlay}>
          {/* The dimmed area closes the picker, like Cancelar */}
          <div className="dayPickerBackdrop" onClick={this.onCancel} />
          { dayPicker }
        </div>
      ) : (
        null
      );
    } else {
      element = dayPicker;
    }

    return element;
  }


  overlay: ?HTMLElement = null;

  setOverlay = (el: ?HTMLElement) => { this.overlay = el; };

  onChangeMonth = (data, direction) => {
    let month;
    if (direction === 'prev') {
      data.onPreviousClick();
      month = data.previousMonth;
    } else {
      data.onNextClick();
      month = data.nextMonth;
    }

    if (this.props.onMonthChange) {
      this.props.onMonthChange(month);
    }
    return data;
  };

  isWeekDayDisabled = (isoWeekDay: number): boolean => {
    if (isoWeekDay === 6) return !!this.props.disableSaturday;
    if (isoWeekDay === 7) return !!this.props.disableSunday;
    return false;
  };

  // Snap a hidden day back to the nearest visible day. Friday (isoWeekday 5) is never
  // disabled, so walking backwards always terminates in at most two steps.
  snapToVisibleDay = (date: moment.Moment): moment.Moment => {
    let nDate = moment(date);
    while (this.isWeekDayDisabled(nDate.isoWeekday())) {
      nDate = nDate.subtract(1, 'day');
    }
    return nDate;
  };

  handleSelectDay = (date) => {
    if (this.props.onSelectDay) {
      this.props.onSelectDay(this.snapToVisibleDay(date));
      if (this.props.acceptOnSelect) {
        this.props.onAccept();
      }
    }
  };

  onCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  handleCaptionClick = (mode: 'year' | 'month') => () => {
    if (!this.props.advancedMode) return;
    if (mode === 'year') {
      this.setState({ yearMode: true, monthMode: false });
    } else {
      this.setState({ yearMode: false, monthMode: true });
    }
  };

  setCalendarMode = () => this.setState({ yearMode: false, monthMode: false });

  onSetYear = (year: number) => () => {
    const dt = moment(this.props.selectedDate)
      .set('year', year)
      .toDate();

    if (this.props.onSelectYear) {
      this.props.onSelectYear(dt);
    }

    this.setState({ montSelectYear: year, yearMode: false, monthMode: true });
  };

  onPaginateYear = (next: boolean) => () => {
    if (next) {
      this.setState({
        yearStart: this.state.yearStart + 12,
        yearEnd: this.state.yearStart + 24,
      });
    } else {
      const y = this.state.yearStart - 12;
      this.setState({
        yearStart: y,
        yearEnd: y + 12,
      });
    }
  };

  onPaginateMonthYear = (next: boolean) => () => {
    if (next) {
      this.setState({ montSelectYear: this.state.montSelectYear + 1 });
    } else {
      this.setState({ montSelectYear: this.state.montSelectYear - 1 });
    }
  };

  onSetMonth = (month: number, year: number) => () => {
    const dt = moment(this.props.selectedDate)
      .set('year', year)
      .set('month', month)
      .toDate();

    if (this.props.onMonthChange) {
      this.props.onMonthChange(dt);
    }
    if (this.props.onSelectMonth) {
      this.props.onSelectMonth(dt);
    }

    this.setCalendarMode();
  };

  onAccept = () => {
    if (this.props.onAccept) {
      this.props.onAccept();
    }
  };

  onTodayClick = () => {
    if (this.props.onSelectDay) {
      this.props.onSelectDay(this.snapToVisibleDay(moment()));
      if (this.props.acceptOnSelect) {
        this.props.onAccept();
      }
    }
  };
}

export default withTranslation()(CustomDayPicker);
